import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { pricingConfig } from '../utils/mockData';
import { Sparkles, TrendingDown, Zap } from 'lucide-react';

const BookingCalculator = ({ onBookingDataChange, showbtn }) => {
  const navigate = useNavigate();


  const getSavedBookingData = () => {
    try {
      const raw = localStorage.getItem('bookingData');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const savedData = getSavedBookingData();
  const initialServiceType = savedData?.serviceType || 'standard';
  const initialCleanType = (() => {
    if (initialServiceType !== 'vacate') {
      return savedData?.cleanType || 'standard';
    }

    const savedCarpetOption =
      typeof savedData?.carpetOption === 'string' ? savedData.carpetOption.trim().toLowerCase() : '';

    if (savedCarpetOption === 'with carpet') return 'deep';
    if (savedCarpetOption === 'without carpet') return 'standard';

    if (savedData?.cleanType === 'deep' || savedData?.cleanType === 'standard') {
      return savedData.cleanType;
    }

    return 'standard';
  })();

  const [bookingType, setBookingType] = useState(savedData?.bookingType || 'size');
  const [bedrooms, setBedrooms] = useState(savedData?.bedrooms || '1');
  const [bathrooms, setBathrooms] = useState(savedData?.bathrooms || '1');
  const [hours, setHours] = useState(savedData?.hours || '3');
  const [minutes, setMinutes] = useState(savedData?.minutes || '0');
  const [cleanType, setCleanType] = useState(initialCleanType);
  const [serviceType, setServiceType] = useState(initialServiceType);
  const [frequency, setFrequency] = useState(savedData?.frequency || 'once');
  const [price, setPrice] = useState(savedData?.price || 0);
  const [isPriceAnimating, setIsPriceAnimating] = useState(false);
  const [addExtraHours, setAddExtraHours] = useState(Boolean(savedData?.addExtraHours));
  const [extraHours, setExtraHours] = useState(savedData?.extraHours ? String(savedData.extraHours) : '1');

  const sizeRateIndex = useMemo(() => {
    const toPairs = (ratesObj) =>
      Object.keys(ratesObj || {})
        .map((k) => {
          const [bedStr, bathStr] = String(k).split('_');
          const bed = Number(bedStr);
          const bath = Number(bathStr);
          if (!Number.isFinite(bed) || !Number.isFinite(bath)) return null;
          return { bed, bath, key: k };
        })
        .filter(Boolean);

    const baseRates = pricingConfig?.baseRates || {};
    if (bookingType !== 'size') return { pairs: [], bedrooms: [], bathroomsByBedroom: new Map() };

    if (serviceType === 'vacate') {
      const map = baseRates?.vacate?.rates?.noCarpet || baseRates?.vacate?.rates?.withCarpet || {};
      const pairs = toPairs(map);
      const bedrooms = Array.from(new Set(pairs.map((p) => p.bed))).sort((a, b) => a - b);
      const bathroomsByBedroom = new Map();
      pairs.forEach((p) => {
        const existing = bathroomsByBedroom.get(p.bed) || [];
        existing.push(p.bath);
        bathroomsByBedroom.set(p.bed, existing);
      });
      bathroomsByBedroom.forEach((vals, bed) => {
        bathroomsByBedroom.set(bed, Array.from(new Set(vals)).sort((a, b) => a - b));
      });
      return { pairs, bedrooms, bathroomsByBedroom };
    }

    if (serviceType === 'deep') {
      const map = baseRates?.deep?.rates || {};
      const pairs = toPairs(map);
      const bedrooms = Array.from(new Set(pairs.map((p) => p.bed))).sort((a, b) => a - b);
      const bathroomsByBedroom = new Map();
      pairs.forEach((p) => {
        const existing = bathroomsByBedroom.get(p.bed) || [];
        existing.push(p.bath);
        bathroomsByBedroom.set(p.bed, existing);
      });
      bathroomsByBedroom.forEach((vals, bed) => {
        bathroomsByBedroom.set(bed, Array.from(new Set(vals)).sort((a, b) => a - b));
      });
      return { pairs, bedrooms, bathroomsByBedroom };
    }

    const map = baseRates?.standard?.rates || {};
    const pairs = toPairs(map);
    const bedrooms = Array.from(new Set(pairs.map((p) => p.bed))).sort((a, b) => a - b);
    const bathroomsByBedroom = new Map();
    pairs.forEach((p) => {
      const existing = bathroomsByBedroom.get(p.bed) || [];
      existing.push(p.bath);
      bathroomsByBedroom.set(p.bed, existing);
    });
    bathroomsByBedroom.forEach((vals, bed) => {
      bathroomsByBedroom.set(bed, Array.from(new Set(vals)).sort((a, b) => a - b));
    });
    return { pairs, bedrooms, bathroomsByBedroom };
  }, [bookingType, serviceType]);

  useEffect(() => {
    if (bookingType !== 'size') return;
    if (!sizeRateIndex.bedrooms?.length) return;

    const currentBed = Number(bedrooms);
    const currentBath = Number(bathrooms);

    const nextBed = sizeRateIndex.bedrooms.includes(currentBed)
      ? currentBed
      : sizeRateIndex.bedrooms[0];

    if (String(nextBed) !== String(bedrooms)) {
      setBedrooms(String(nextBed));
      return;
    }

    const allowedBaths = sizeRateIndex.bathroomsByBedroom.get(nextBed) || [];
    if (!allowedBaths.length) return;

    const nextBath = allowedBaths.includes(currentBath) ? currentBath : allowedBaths[0];
    if (String(nextBath) !== String(bathrooms)) {
      setBathrooms(String(nextBath));
    }
  }, [bookingType, serviceType, sizeRateIndex, bedrooms, bathrooms]);

  useEffect(() => {
    if (bookingType !== 'size' || serviceType !== 'standard') {
      setAddExtraHours(false);
      setExtraHours('1');
    }
  }, [bookingType, serviceType]);

  useEffect(() => {
    calculatePrice();
  }, [bookingType, bedrooms, bathrooms, hours, minutes, cleanType, serviceType, frequency, addExtraHours, extraHours]);

  const getFrequencyDiscountValue = () => {
    if (frequency === 'once' || serviceType === 'vacate') return 0;
    const rateEntries = Object.values(pricingConfig.baseRates?.standard?.rates || {});
    const sample = rateEntries.find((entry) =>
      entry && typeof entry === 'object' && entry.onceOff && entry[frequency]
    );
    if (!sample) return 0;
    const discount = 1 - sample[frequency] / sample.onceOff;
    return Number.isFinite(discount) && discount > 0 ? discount : 0;
  };

  const calculatePrice = () => {
    setIsPriceAnimating(true);
    let basePrice = 0;
    const config = pricingConfig.baseRates;
    const isVacate = serviceType === 'vacate';
    const isDeep = serviceType === 'deep';
    const discountValue = getFrequencyDiscountValue();
    let appliedFrequencyRate = false;

    if (bookingType === 'size') {
      const sizeKey = `${bedrooms}_${bathrooms}`;
      if (isVacate) {
        const withCarpet = cleanType === 'deep';
        const vacateRates = config.vacate?.rates?.noCarpet || {};
        const vacateWithCarpetRates = config.vacate?.rates?.withCarpet || {};
        basePrice = withCarpet
          ? vacateWithCarpetRates[sizeKey]
          : vacateRates[sizeKey];
      } else if (isDeep) {
        basePrice = config.deep?.rates?.[sizeKey];
      } else {
        const rateEntry = config.standard?.rates?.[sizeKey];
        if (rateEntry && typeof rateEntry === 'object') {
          const rateKey = frequency === 'once' ? 'onceOff' : frequency;
          basePrice = rateEntry[rateKey] ?? rateEntry.onceOff;
          appliedFrequencyRate = frequency !== 'once' && rateEntry[rateKey] != null;
        } else {
          basePrice = rateEntry;
        }
      }
      if (!basePrice) basePrice = 0;
    } else {
      const type = isDeep ? 'deep' : 'standard';
      const totalHours = parseFloat(hours) + parseFloat(minutes) / 60;
      const rate = config[type]?.hourly || 0;
      basePrice = rate * totalHours;
    }

    if (frequency !== 'once' && !appliedFrequencyRate && discountValue > 0) {
      basePrice = basePrice * (1 - discountValue);
    }

    if (bookingType === 'size' && serviceType === 'standard' && addExtraHours) {
      const extraHoursCount = Number(extraHours);
      if (Number.isFinite(extraHoursCount) && extraHoursCount > 0) {
        let extraCost = extraHoursCount * 70;
        if (frequency !== 'once' && discountValue > 0) {
          extraCost = extraCost * (1 - discountValue);
        }
        basePrice += extraCost;
      }
    }

    const finalPrice = Math.round(basePrice);
    setPrice(finalPrice);

    const carpetOption = serviceType === 'vacate'
      ? (cleanType === 'deep' ? 'With Carpet' : 'Without Carpet')
      : null;
    const normalizedCleanType = serviceType === 'vacate' ? 'vacate' : serviceType;

    const bookingData = {
      bookingType,
      bedrooms: bookingType === 'size' ? bedrooms : null,
      bathrooms: bookingType === 'size' ? bathrooms : null,
      hours: bookingType === 'hourly' ? hours : null,
      minutes: bookingType === 'hourly' ? minutes : null,
      cleanType: normalizedCleanType,
      carpetOption,
      addExtraHours,
      extraHours: addExtraHours ? Number(extraHours) : 0,
      serviceType,
      frequency,
      price: finalPrice
    };

    if (onBookingDataChange) {
      onBookingDataChange(bookingData);
    }
    setTimeout(() => setIsPriceAnimating(false), 300);
  };

  const getDiscountPercentage = () => {
    if (frequency === 'once' || serviceType === 'vacate') return 0;
    const rateEntries = Object.values(pricingConfig.baseRates?.standard?.rates || {});
    const sample = rateEntries.find((entry) =>
      entry && typeof entry === 'object' && entry.onceOff && entry[frequency]
    );
    if (!sample) return 0;
    const percentage = Math.round((1 - sample[frequency] / sample.onceOff) * 100);
    return Math.max(0, percentage);
  };

  const handleBookNow = async () => {
    const carpetOption = serviceType === 'vacate'
      ? (cleanType === 'deep' ? 'With Carpet' : 'Without Carpet')
      : null;
    const normalizedCleanType = serviceType === 'vacate' ? 'vacate' : serviceType;

    const bookingData = {
      bookingType,
      bedrooms: bookingType === 'size' ? bedrooms : null,
      bathrooms: bookingType === 'size' ? bathrooms : null,
      hours: bookingType === 'hourly' ? hours : null,
      minutes: bookingType === 'hourly' ? minutes : null,
      cleanType: normalizedCleanType,
      carpetOption,
      addExtraHours,
      extraHours: addExtraHours ? Number(extraHours) : 0,
      serviceType,
      frequency,
      price
    };
    localStorage.setItem('bookingData', JSON.stringify(bookingData));

    navigate('/book');
  };

  const discountPercentage = getDiscountPercentage();

  return (
    <Card className="p-8 shadow-2xl border-0 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 backdrop-blur-sm overflow-hidden relative">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl -z-0 transform transition-transform duration-300 hover:scale-105"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-purple-400/10 to-pink-400/10 rounded-full blur-3xl -z-0 transform transition-transform duration-300 hover:scale-105"></div>

      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 mb-3">
            {/* <Sparkles className="w-6 h-6 text-blue-600 animate-pulse" /> */}
            <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Book Your Clean Today
            </h3>
            {/* <Sparkles className="w-6 h-6 text-purple-600 animate-pulse" /> */}
          </div>
          <p className="text-slate-600 text-lg">Get an instant quote in seconds</p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Form */}
          <div className="space-y-6">
            <Tabs value={bookingType} onValueChange={setBookingType} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-white shadow-sm p-1 h-12">
                <TabsTrigger 
                  value="size" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-500 data-[state=active]:text-white transition-colors duration-300 rounded-md font-semibold"
                >
                  By Size
                </TabsTrigger>
                <TabsTrigger 
                  value="hourly"
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white transition-colors duration-300 rounded-md font-semibold"
                >
                  Hourly
                </TabsTrigger>
              </TabsList>

              <TabsContent value="size" className="space-y-5 mt-6">
                {/* Bedrooms */}
                <div>
                  <Label className="text-slate-700 font-semibold mb-2 flex items-center">
                    <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    Bedrooms
                  </Label>
                  <Select value={bedrooms} onValueChange={setBedrooms}>
                    <SelectTrigger className="mt-2 h-12 border-2 border-slate-200 hover:border-blue-400 focus:border-blue-600 transition-colors duration-300 bg-white shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(sizeRateIndex.bedrooms.length ? sizeRateIndex.bedrooms : [1, 2, 3, 4, 5, 6]).map((num) => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} Bedroom{num > 1 ? 's' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bathrooms */}
                <div>
                  <Label className="text-slate-700 font-semibold mb-2 flex items-center">
                    <span className="inline-block w-2 h-2 bg-purple-600 rounded-full mr-2"></span>
                    Bathrooms
                  </Label>
                  <Select value={bathrooms} onValueChange={setBathrooms}>
                    <SelectTrigger className="mt-2 h-12 border-2 border-slate-200 hover:border-purple-400 focus:border-purple-600 transition-colors duration-300 bg-white shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(
                        sizeRateIndex.bathroomsByBedroom.get(Number(bedrooms))?.length
                          ? sizeRateIndex.bathroomsByBedroom.get(Number(bedrooms))
                          : [1, 2, 3, 4, 5, 6]
                      ).map((num) => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} Bathroom{num > 1 ? 's' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="hourly" className="space-y-5 mt-6">
                {/* Hours */}
                <div>
                  <Label className="text-slate-700 font-semibold mb-2 flex items-center">
                    <span className="inline-block w-2 h-2 bg-purple-600 rounded-full mr-2"></span>
                    Hours
                  </Label>
                  <Select value={hours} onValueChange={setHours}>
                    <SelectTrigger className="mt-2 h-12 border-2 border-slate-200 hover:border-purple-400 focus:border-purple-600 transition-colors duration-300 bg-white shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8].map(num => <SelectItem key={num} value={num.toString()}>{num} Hours</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Minutes */}
                <div>
                  <Label className="text-slate-700 font-semibold mb-2 flex items-center">
                    <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    Minutes
                  </Label>
                  <Select value={minutes} onValueChange={setMinutes}>
                    <SelectTrigger className="mt-2 h-12 border-2 border-slate-200 hover:border-blue-400 focus:border-blue-600 transition-colors duration-300 bg-white shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 Minutes</SelectItem>
                      <SelectItem value="30">30 Minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>

            {/* Service Type */}
            <div>
              <Label className="text-slate-700 font-semibold mb-2 flex items-center">
                <Sparkles className="w-4 h-4 text-blue-600 mr-2" />
                Service Type
              </Label>
              <Select value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger className="mt-2 h-12 border-2 border-slate-200 hover:border-blue-400 focus:border-blue-600 transition-colors duration-300 bg-white shadow-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {bookingType === 'size' ? (
                    <>
                      <SelectItem value="standard">Standard Clean</SelectItem>
                      <SelectItem value="deep">Deep Clean</SelectItem>
                      <SelectItem value="vacate">Vacate Clean</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="standard">Standard Clean</SelectItem>
                      <SelectItem value="deep">Deep Clean</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Carpet Option - Only show if vacate clean is selected and booking type is size */}
            {serviceType === 'vacate' && bookingType === 'size' && (
              <div>
                <Label className="text-slate-700 font-semibold mb-2 flex items-center">
                  <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                  Carpet Option
                </Label>
                <Select value={cleanType} onValueChange={setCleanType}>
                  <SelectTrigger className="mt-2 h-12 border-2 border-slate-200 hover:border-blue-400 focus:border-blue-600 transition-colors duration-300 bg-white shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Without Carpet</SelectItem>
                    <SelectItem value="deep">With Carpet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Frequency */}
            {serviceType !== 'vacate' && (
              <div>
                <Label className="text-slate-700 font-semibold mb-2 flex items-center">
                  <TrendingDown className="w-4 h-4 text-green-600 mr-2" />
                  Frequency {discountPercentage > 0 && (
                    <span className="ml-2 text-green-600 text-sm">(Save {discountPercentage}%!)</span>
                  )}
                </Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger className="mt-2 h-12 border-2 border-slate-200 hover:border-green-400 focus:border-green-600 transition-colors duration-300 bg-white shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Once Off</SelectItem>
                    <SelectItem value="weekly">
                      <span className="flex items-center justify-between w-full">
                        Weekly <span className="ml-2 text-green-600 font-semibold">15% OFF</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="fortnightly">
                      <span className="flex items-center justify-between w-full">
                        Fortnightly <span className="ml-2 text-green-600 font-semibold">10% OFF</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="monthly">
                      <span className="flex items-center justify-between w-full">
                        Monthly <span className="ml-2 text-green-600 font-semibold">10% OFF</span>
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Extra Hours (Standard size bookings) */}
            {bookingType === 'size' && serviceType === 'standard' && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="text-slate-800 font-semibold flex items-center">
                      <Zap className="w-4 h-4 text-blue-600 mr-2" />
                      Add Extra Hours
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">
                      Add additional cleaning time for extras at an hourly rate.
                      (Inside Oven, Inside Fridge, Inside Windows, Outside Windows etc.)
                    </p>
                  </div>
                  <div className="pt-1">
                    <Checkbox
                      checked={addExtraHours}
                      onCheckedChange={(checked) => setAddExtraHours(Boolean(checked))}
                      aria-label="Add Extra Hours"
                    />
                  </div>
                </div>

                {addExtraHours && (
                  <div className="mt-4">
                    <Label className="text-slate-700 font-semibold mb-2 block">Extra Hours</Label>
                    <Select value={extraHours} onValueChange={setExtraHours}>
                      <SelectTrigger className="h-12 border-2 border-slate-200 hover:border-blue-400 focus:border-blue-600 transition-colors duration-300 bg-white shadow-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5,6,7,8].map((num) => (
                          <SelectItem key={num} value={String(num)}>
                            {num} hour{num > 1 ? 's' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Price & Booking */}
          <div className="space-y-6">
            {/* Price Display */}
            <div className={`bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 rounded-2xl p-8 shadow-2xl border-0 transform transition-transform duration-300 ${isPriceAnimating ? 'scale-105' : 'scale-100'}`}>
              <div className="text-center">
                <p className="text-sm text-white/90 mb-2 font-medium uppercase tracking-wider">Estimated Price</p>
                <div className="flex items-center justify-center space-x-3">
                  <p className={`text-6xl font-bold text-white transition-transform duration-300 ${isPriceAnimating ? 'scale-110' : 'scale-100'}`}>
                    ${price}
                  </p>
                </div>
                {discountPercentage > 0 && (
                  <div className="mt-3 inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                    <TrendingDown className="w-4 h-4 text-green-300" />
                    <span className="text-white text-sm font-semibold">
                      You're saving {discountPercentage}% with {frequency} booking!
                    </span>
                  </div>
                )}
              </div>
            </div>

            {showbtn == true ? (

              <>
            <Button 
              onClick={handleBookNow}
              className="w-full bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 hover:from-blue-700 hover:via-blue-600 hover:to-purple-700 text-white text-lg py-7 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-transform duration-300 font-bold uppercase tracking-wide"
            >
              {/* <Sparkles className="w-5 h-5 mr-2" /> */}
              Book Now & Save
              {/* <Sparkles className="w-5 h-5 ml-2" /> */}
            </Button>

            <p className="text-left text-sm text-slate-700">
              🔒 Secure booking <br/> 💳 Easy payment <br/> ⚡ Instant confirmation
            </p>
              </>
            ):
            <></>}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default BookingCalculator;
